import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Amazing air machines",
  "description": "",
  "published": "2012-02-08T00:00:00.000Z",
  "redirect_from": ["/blog/amazing-air-machines/swizec/3640"],
  "categories": "Heat exchanger, Lightning, Massachusetts Institute of Technology, Nikola Tesla, Resonance, Technology, Tesla coil, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`As a computer guy I often forget that there is a whole world out there beyond pictures of cats, big-O and trying to create things that should be impossible. When it strikes my fancy, I sometimes realize there's also mathematics.`}</p>
    <p>{`But sometimes you come across something so simple, so very much in the realm of macro-physics, it blows your mind and you spend a couple of hours looking for even more pieces of remarkably simple, yet incomprehensible, technology.`}</p>
    <h2 {...{
      "id": "tesla-valve-with-no-moving-parts"
    }}><a parentName="h2" {...{
        "href": "http://en.wikipedia.org/wiki/Tesla_valve",
        "title": "Tesla valve"
      }}>{`Tesla valve`}</a>{` with no moving parts`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/02/1329559us-teslas-valvular-conduit-figure25.gif",
        "alt": "Tesla valve patent",
        "title": "Tesla valve patent"
      }}></img></p>
    <p>{`It lets some air through so it's not a perfect valve, but it is still perfectly useful for a lot of applications. Honestly, I don't understand `}<em parentName="p">{`what`}</em>{` goes on in there, but the idea seems to be to have the fluid back in on itself and pretty much stop itself. The stronger the force in one direction, the stronger the force in the opposite direction. Brilliant.`}</p>
    <p>{`Somebody printed the valve and gave it a test`}</p>
    <lite-youtube {...{
      "videoid": "25ElCGkQLTU",
      "videostartat": "0"
    }}></lite-youtube>
    <h2 {...{
      "id": "vortex-tube"
    }}><a parentName="h2" {...{
        "href": "http://en.wikipedia.org/wiki/Vortex_tube",
        "title": "Vortex tube"
      }}>{`Vortex tube`}</a></h2>
    <p>{`Cool things you can do with air and no moving parts don't stop there though. There's also something called a Vortex tube.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/02/Tube_de_Ranque-Hilsch9.png",
        "alt": "A vortex tube",
        "title": "A vortex tube"
      }}></img></p>
    <p>{`The vortex tube accepts pressurised gas on one end and then with no moving parts separates this flow into a stream of very hot gas and a stream of very cold gas. It could potentially be used to both heat and cool some things ... completely without moving parts. Just cool, hardcore, flow dynamics.`}</p>
    <p>{`It baffles the brain. It really does. So much so that physicists have yet to reach a consensus on how a vortex tube even works.`}</p>
    <h2 {...{
      "id": "thermoacoustic-heat-engine"
    }}>{`Thermoacoustic `}<a parentName="h2" {...{
        "href": "http://en.wikipedia.org/wiki/Heat_engine",
        "title": "Heat engine"
      }}>{`heat engine`}</a></h2>
    <p>{`And then it gets even better with a thermoacoustic heat engine - using high energy sound waves to pump heat from one end of a tube to another, or even using the heat difference to produce a loud noise.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/02/400px-Machine-thermoacoustique8.png",
        "alt": "Thermoacoustics",
        "title": "Thermoacoustics"
      }}></img></p>
    <blockquote>
      <p parentName="blockquote">{`A thermoacoustic device basically consists of `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Heat_exchanger",
          "title": "Heat exchanger"
        }}>{`heat exchangers`}</a>{`, a `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Resonator",
          "title": "Resonator"
        }}>{`resonator`}</a>{`, and a stack (on standing wave devices) or `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Regenerative_heat_exchanger",
          "title": "Regenerative heat exchanger"
        }}>{`regenerator`}</a>{` (on travelling wave devices). Depending on the type of engine a `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Speaker_driver",
          "title": "Speaker driver"
        }}>{`driver`}</a>{` or `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Loudspeaker",
          "title": "Loudspeaker"
        }}>{`loudspeaker`}</a>{` might be used as well to generate sound waves.`}</p>
      <p parentName="blockquote">{`Consider a tube closed at both ends. Interference can occur between two waves traveling in opposite directions at certain frequencies. The interference causes `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Resonance",
          "title": "Resonance"
        }}>{`resonance`}</a>{` creating a standing wave. Resonance only occurs at certain frequencies called `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Resonance",
          "title": "Resonance"
        }}>{`resonance frequencies`}</a>{`, and these are mainly determined by the length of the resonator.`}</p>
      <p parentName="blockquote">{`The stack is a part consisting of small parallel channels. When the stack is placed at a certain location in the resonator, while having a standing wave in the resonator, a temperature difference can be measured across the stack. By placing heat exchangers at each side of the stack, heat can be moved. The opposite is possible as well, by creating a temperature difference across the stack, a `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Sound",
          "title": "Sound"
        }}>{`sound wave`}</a>{` can be induced. The first example is a simple heat pump, while the second is a prime mover.`}</p>
    </blockquote>
    <h2 {...{
      "id": "jet-engines-with-no-moving-parts"
    }}>{`Jet engines with no moving parts`}</h2>
    <p>{`And apparently there's a whole subset of jet engines that don't use any moving parts. Of course you've heard of a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Scramjet",
        "title": "Scramjet"
      }}>{`scram jet`}</a>{`, but there's more.`}</p>
    <p>{`There exists such a thing as a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Valveless_pulse_jet",
        "title": "Valveless pulse jet"
      }}>{`valveless pulse jet`}</a>{` - I don't know exactly what this means, but it sounds pretty amazing.`}</p>
    <lite-youtube {...{
      "videoid": "EEHw9lInIfg",
      "videostartat": "0"
    }}></lite-youtube>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.techeblog.com/index.php/tech-gadget/5-mind-blowing-quantum-levitation-experiments"
        }}>{`5 Mind-Blowing Quantum Levitation Experiments`}</a>{` (techeblog.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.techeblog.com/index.php/tech-gadget/guy-builds-awesome-twin-tesla-coil-setup-at-home"
        }}>{`Guy Builds Awesome Twin Tesla Coil Setup at Home`}</a>{` (techeblog.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://hackaday.com/2012/02/07/diy-solid-state-tesla-coil/"
        }}>{`DIY Solid State Tesla Coil`}</a>{` (hackaday.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.geekologie.com/2011/11/sing-in-the-rain-kid-makes-musical-tesla.php"
        }}>{`Sing In The Rain!: Kid Makes Musical Tesla Coil Hat`}</a>{` (geekologie.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.engadget.com/2011/11/15/mit-student-creates-tesla-coil-musical-hat-for-halloween-surviv/"
        }}>{`MIT student creates tesla coil musical hat for Halloween, survives`}</a>{` (engadget.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=c9d8239c-49a5-487c-af18-e412f864313e",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      